import { useState, useMemo, useCallback } from 'react';
import { AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, Button, cn } from '@loader/ui';

import { useOnboardingSteps } from './useOnboardingSteps';
import { useOnboardingStore } from './useOnboardingStore';

const ALLOWED_MEDIA_HOSTS = ['assets.loada.io', 'loada.io'];
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'];

function isSafeMediaUrl(url: string): boolean {
  try {
    return ALLOWED_MEDIA_HOSTS.includes(new URL(url).hostname);
  } catch {
    return false;
  }
}

function isImageUrl(url: string): boolean {
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    return IMAGE_EXTENSIONS.some((ext) => pathname.endsWith(ext));
  } catch {
    return false;
  }
}

function MediaRenderer({
  url,
  alt,
  onError,
}: {
  url: string | undefined;
  alt: string;
  onError: () => void;
}) {
  if (!url) {
    return (
      <div className="flex flex-col items-center gap-2 text-muted-foreground">
        <AlertCircle className="h-8 w-8" />
        <span className="text-sm font-medium">Media could not be loaded</span>
      </div>
    );
  }

  if (isImageUrl(url)) {
    return (
      <img
        key={url}
        className="max-h-full max-w-full object-contain"
        src={url}
        alt={alt}
        onError={onError}
      />
    );
  }

  return (
    <video
      key={url}
      className="max-h-full max-w-full rounded-lg object-contain shadow-lg"
      src={url}
      autoPlay
      loop
      muted
      playsInline
      onError={onError}
    />
  );
}

export function OnboardingDialog() {
  const { isOpen, close, complete } = useOnboardingStore();
  const steps = useOnboardingSteps();
  const [step, setStep] = useState(0);
  const [mediaError, setMediaError] = useState(false);

  const current = steps[step];
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;
  const safeMediaUrl = useMemo(
    () => (current?.mediaUrl && isSafeMediaUrl(current.mediaUrl) ? current.mediaUrl : undefined),
    [current?.mediaUrl],
  );
  const isImage = safeMediaUrl ? isImageUrl(safeMediaUrl) : false;
  const handleMediaError = useCallback(() => setMediaError(true), []);

  const goToStep = (i: number) => {
    setMediaError(false);
    setStep(i);
  };

  const handleNext = () => {
    if (isLast) {
      complete();
    } else {
      goToStep(step + 1);
    }
  };

  const handleBack = () => {
    if (!isFirst) goToStep(step - 1);
  };

  const handleSkip = () => {
    close();
  };

  // Reset step to 0 after the dialog close animation finishes,
  // so the first slide doesn't flash during the transition.
  const handleAnimationEnd = () => {
    if (!isOpen) {
      setStep(0);
      setMediaError(false);
    }
  };

  return (
    <Dialog open={isOpen && !!current} onOpenChange={(open) => !open && handleSkip()}>
      <DialogContent
        className="!max-w-4xl !gap-0 !p-0 h-[min(720px,90dvh)] !flex !flex-col overflow-hidden"
        showCloseButton={false}
        onAnimationEnd={handleAnimationEnd}
      >
        {current && (
          <>
            {/* Media - takes remaining space, shrinks when viewport is short */}
            {(safeMediaUrl || mediaError) && (
              <div
                className={cn(
                  'flex min-h-0 flex-1 items-center justify-center overflow-hidden',
                  isImage ? 'mx-12 mt-12' : 'm-4 mb-0',
                )}
              >
                <MediaRenderer
                  url={mediaError ? undefined : safeMediaUrl}
                  alt={current.title}
                  onError={handleMediaError}
                />
              </div>
            )}

            {/* Text + controls - fixed height, never shrinks */}
            <div className="shrink-0 px-6 pb-6">
              <div className="flex flex-col justify-center gap-1 py-4 text-center">
                <h2 className="text-[clamp(1.25rem,3.5dvh,1.5rem)] font-semibold tracking-tight">{current.title}</h2>
                <p className="text-[clamp(0.875rem,2.5dvh,1.125rem)] text-muted-foreground whitespace-pre-line">
                  {current.description}
                </p>
              </div>

              {/* Navigation */}
              <div className="relative flex items-center justify-center">
                <div className="absolute left-0">
                  <Button variant="ghost" size="sm" onClick={handleSkip}>
                    Skip
                  </Button>
                </div>
                <div className="flex items-center gap-1.5">
                  {steps.map((_, i) => (
                    <button
                      key={i}
                      aria-label={`Go to step ${i + 1}`}
                      onClick={() => goToStep(i)}
                      className={cn(
                        'h-1.5 rounded-full transition-all',
                        i === step ? 'w-4 bg-primary' : 'w-1.5 bg-muted-foreground/30',
                      )}
                    />
                  ))}
                </div>
                <div className="absolute right-0 flex gap-2">
                  {!isFirst && (
                    <Button variant="outline" size="sm" onClick={handleBack}>
                      Back
                    </Button>
                  )}
                  <Button size="sm" onClick={handleNext}>
                    {isLast ? 'Get Started' : 'Next'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
